﻿import { getUserVisibleOrDefaultExceptionMessage, timeoutAsync } from '@paragon/client-tools';
import { ComponentDesign, LumberWithStockLengthAndPrice } from '@paragon/contract-library';
import { BackgroundOptimizationOperation, CostWeights, designService } from '@paragon/data-connections';
import { hasCanUsePlatesPermission } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getAppMonitor } from '../../monitor';
import { waitForComponentDesignToFinishSaving } from '../middlewares/saveComponentDesignMiddleware';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import {
  getMissingLumberPriceRowsForActiveComponent,
  getMissingPlatePriceRowsForActiveComponent,
} from '../selectors/missingLumberPriceSelectors';
import { getOptimizationRunForActiveComponent } from '../selectors/optimizationProgressSelectors';
import { setComponent } from '../slices/componentDesignsSlice';
import {
  clearOptimizationCostComparison,
  setOptimizationCostComparison,
} from '../slices/optimizationCostComparisonSlice';
import {
  optimizationRunFailed,
  optimizationRunPolled,
  optimizationRunStarted,
  optimizedDesignAdopted,
} from '../slices/optimizationProgressSlice';
import {
  mapCostFactorsToCostWeights,
  mapMissingLumberPricesToUserSpecified,
  mapMissingPlatePricesToUserSpecified,
  missingOptimizerPricesFailed,
  missingOptimizerPricesLoaded,
  missingOptimizerPricesRequested,
} from '../slices/optimizationSettingsSlice';
import { loadLumberPrices } from './loadActions';
import { recordErrorDuringAnalysis } from './longRunningAnalysisActions';
import { anyJacksHaveWorkingChanges } from './paragonProcessActions';
import { showUpgradeAccountDialogIfQuotaMetAndNotAllowedToExceed } from './popupActions';
import { AsyncThunkActionVoid } from './thunkActions';

export const optimizeActiveComponentDesign = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const { levers, labor, maxPlies, bracingPrice } = getState().optimizationSettings;

  const activeComponentDesign = getActiveComponentDesign(getState());
  if (!activeComponentDesign) throw new Error('Could not find active component.');

  const analyzingWillAddToQuota = !getState().analysisHistory[activeComponentDesign.guid];
  if (analyzingWillAddToQuota) {
    const dialogShown = dispatch(showUpgradeAccountDialogIfQuotaMetAndNotAllowedToExceed());
    if (dialogShown) return;
  }

  await dispatch(
    optimize(activeComponentDesign, {
      allowOptimizationChordResizing: levers.resizeChords,
      varySplicing: levers.varySplicing,
      optimizePlating: levers.plating,
      runExhaustivePlateSearch: levers.plating && levers.exhaustivePlating,
      maxOptimizationPlies: levers.varyPlyCount ? maxPlies : null,
      costWeights: mapCostFactorsToCostWeights(labor, bracingPrice),
      userSpecifiedLumberPrices: mapMissingLumberPricesToUserSpecified(
        getMissingLumberPriceRowsForActiveComponent(getState()),
      ),
      userSpecifiedPlatePrices: mapMissingPlatePricesToUserSpecified(
        getMissingPlatePriceRowsForActiveComponent(getState()),
      ),
    }),
  );
};

/**
 * Takes the truss a finished run settled on and makes it the component's design.
 */
export const adoptOptimizedDesign = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const run = getOptimizationRunForActiveComponent(getState());
  if (run == null || run.status !== 'completed' || run.bestTrussSoFar == null) return;

  const { componentDesignGuid, bestTrussSoFar: optimizedTruss } = run;

  try {
    // The optimizer is free to pick lumber the design was not using, whose price nothing has had reason to fetch yet.
    await dispatch(loadLumberPrices(optimizedTruss.members.map((member) => member.lumber)));

    dispatch(setComponent({ componentDesignGuid, component: optimizedTruss }));
    dispatch(optimizedDesignAdopted({ componentDesignGuid }));
  } catch (error) {
    console.error(error);
    dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Unable to adopt the optimized design.')));
  }
};

interface OptimizationOptions {
  readonly allowOptimizationChordResizing: boolean;
  readonly varySplicing: boolean;
  readonly optimizePlating: boolean;
  readonly runExhaustivePlateSearch: boolean;
  /** Highest ply count the optimizer may try; null keeps the truss's current ply count. */
  readonly maxOptimizationPlies: number | null;
  readonly costWeights: CostWeights;
  readonly userSpecifiedLumberPrices: readonly LumberWithStockLengthAndPrice[];
  readonly userSpecifiedPlatePrices: { readonly [plateType: string]: number };
}

const optimize =
  (componentDesign: ComponentDesign, optimizationOptions: OptimizationOptions): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (await anyJacksHaveWorkingChanges(getState, componentDesign.guid)) {
      dispatch(
        recordErrorDuringAnalysis(
          componentDesign.guid,
          'Unable to generate load cases: Some jack components have unanalyzed changes.',
          { fatal: true },
        ),
      );
      return;
    }

    const timer = getAppMonitor().beginTimedOperation('Optimize');

    dispatch(
      optimizationRunStarted({
        componentDesignGuid: componentDesign.guid,
        componentName: componentDesign.component.name,
      }),
    );

    try {
      await waitForComponentDesignToFinishSaving(componentDesign.guid);

      const { operationGuid } = await designService.componentDesigns.startOptimization(componentDesign, {
        allowOptimizationChordResizing: optimizationOptions.allowOptimizationChordResizing,
        varySplicing: optimizationOptions.varySplicing,
        optimizePlating: hasCanUsePlatesPermission(getState()) && optimizationOptions.optimizePlating,
        runExhaustivePlateSearch: hasCanUsePlatesPermission(getState()) && optimizationOptions.runExhaustivePlateSearch,
        maxOptimizationPlies: optimizationOptions.maxOptimizationPlies,
        costWeights: optimizationOptions.costWeights,
        userSpecifiedLumberPrices: optimizationOptions.userSpecifiedLumberPrices,
        userSpecifiedPlatePrices: optimizationOptions.userSpecifiedPlatePrices,
      });

      const optimizationResponse = await pollForOptimizationResult(operationGuid, (operation) =>
        dispatch(optimizationRunPolled({ componentDesignGuid: componentDesign.guid, operation })),
      );

      dispatch(
        optimizationResponse.costComparison
          ? setOptimizationCostComparison({
              componentDesignGuid: componentDesign.guid,
              comparison: optimizationResponse.costComparison,
            })
          : clearOptimizationCostComparison(),
      );
    } catch (error) {
      const message = getUserVisibleOrDefaultExceptionMessage(error, 'Unable to optimize.');
      dispatch(enqueueError(message));
      dispatch(optimizationRunFailed({ componentDesignGuid: componentDesign.guid, errorMessage: message }));

      dispatch(clearOptimizationCostComparison());
    } finally {
      getAppMonitor().concludeTimedOperation(timer);
    }
  };

const OPTIMIZATION_POLL_INTERVAL_MILLISECONDS = 5000;

/**
 * Polls until the run finishes, reporting every status it reads to `onProgress` -- the operation carries how far the
 * search has gotten and the best truss it has found, not just whether it is done.
 */
async function pollForOptimizationResult(
  operationGuid: string,
  onProgress: (operation: BackgroundOptimizationOperation) => void,
) {
  while (true) {
    const operation = await designService.componentDesigns.getOptimizationOperationStatus(operationGuid);
    onProgress(operation);

    switch (operation.status) {
      case 'Pending':
      case 'InProgress':
        await timeoutAsync(OPTIMIZATION_POLL_INTERVAL_MILLISECONDS);
        break;

      case 'Completed':
        return operation;

      case 'Failed':
        throw new Error('Unable to optimize.');

      default:
        assertExhaustiveSwitch(operation.status);
    }
  }
}

// Fetches the lumber and plate types the optimizer could use that the org hasn't priced, into the panel's settings.
export const loadMissingOptimizerPrices = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const activeComponentDesign = getActiveComponentDesign(getState());
  if (!activeComponentDesign) return;
  const requestedComponentDesignGuid = activeComponentDesign.guid;

  const existingPrices = getState().optimizationSettings.missingOptimizerPrices;
  if (
    (existingPrices.status === 'loaded' || existingPrices.status === 'loading') &&
    existingPrices.componentDesignGuid === requestedComponentDesignGuid
  ) {
    return;
  }

  dispatch(missingOptimizerPricesRequested({ componentDesignGuid: requestedComponentDesignGuid }));

  try {
    const { missingLumberPrices, missingPlatePrices } =
      await designService.componentDesigns.getMissingOptimizerPrices(requestedComponentDesignGuid);
    if (getActiveComponentDesign(getState())?.guid !== requestedComponentDesignGuid) return;
    dispatch(
      missingOptimizerPricesLoaded({
        componentDesignGuid: requestedComponentDesignGuid,
        lumber: missingLumberPrices,
        plates: missingPlatePrices ?? [],
      }),
    );
  } catch (exception) {
    console.error('Failed to load missing optimizer prices', exception);
    if (getActiveComponentDesign(getState())?.guid === requestedComponentDesignGuid) {
      dispatch(missingOptimizerPricesFailed({ componentDesignGuid: requestedComponentDesignGuid }));
    }
  }
};
